import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';
import { SignIn, Eye, Clock, GlobeSimple, SpinnerGap, ArrowLeft } from '@phosphor-icons/react';

const fmtDate = iso => iso ? new Date(iso).toLocaleString('pt-PT') : '-';

export default function AnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/analytics/overview', { params: { days } });
      setRows(data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (user && user.ID_Cliente !== 1) return <Navigate to="/" replace />;

  return (
    <div data-testid="analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Atividade dos Consultores</h1>
          <p className="text-sm text-[#6B7280] mt-1">Logins e páginas mais visitadas — acesso restrito.</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))} data-testid="days-select"
          className="px-3 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white">
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
          <option value={365}>Último ano</option>
        </select>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#9CA3AF]"><SpinnerGap size={28} className="animate-spin inline" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-4 py-3 text-left font-medium">Consultor</th>
                <th className="px-4 py-3 text-right font-medium">Logins</th>
                <th className="px-4 py-3 text-left font-medium">Último login</th>
                <th className="px-4 py-3 text-right font-medium">Páginas</th>
                <th className="px-4 py-3 text-left font-medium">Última visita</th>
                <th className="px-4 py-3 text-center font-medium w-28"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-[#9CA3AF]">Sem registos nesta janela.</td></tr>}
              {rows.map(r => (
                <tr key={r.ID_Cliente} className="hover:bg-[#F9FAFB]" data-testid={`analytics-row-${r.ID_Cliente}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#111827]">{r.NomeCompleto}</div>
                    <div className="text-xs text-[#6B7280]">#{r.ID_Cliente}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-[#0F1829]">{r.total_logins}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{fmtDate(r.last_login)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#00C853]">{r.total_visits}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{fmtDate(r.last_visit)}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => setSelected(r)} data-testid={`view-detail-${r.ID_Cliente}`}
                      className="px-3 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#001f6e] inline-flex items-center gap-1">
                      <Eye size={14} /> Detalhe
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {selected && <AnalyticsDetail cid={selected.ID_Cliente} days={days} onClose={() => setSelected(null)} />}
    </div>
  );
}

function AnalyticsDetail({ cid, days, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/consultant/${cid}`, { params: { days, top_pages: 30 } })
      .then(({ data }) => setData(data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [cid, days]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between bg-[#F9FAFB]">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#E5E7EB]"><ArrowLeft size={16} /></button>
            <h3 className="text-sm font-semibold text-[#111827]">{data?.nome || 'A carregar...'} <span className="text-[#9CA3AF] font-normal ml-2">#{cid}</span></h3>
          </div>
          <span className="text-xs text-[#6B7280]">Janela: {days} dias</span>
        </div>
        {loading || !data ? (
          <div className="py-20 text-center text-[#9CA3AF]"><SpinnerGap size={28} className="animate-spin inline" /></div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 flex items-center gap-3">
                <div className="p-2 rounded-md bg-[#0F1829]/10"><SignIn size={22} className="text-[#0F1829]" weight="duotone" /></div>
                <div>
                  <p className="text-xs text-[#6B7280] uppercase tracking-wide">Logins</p>
                  <p className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{data.total_logins}</p>
                </div>
              </div>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 flex items-center gap-3">
                <div className="p-2 rounded-md bg-green-50"><GlobeSimple size={22} className="text-[#00C853]" weight="duotone" /></div>
                <div>
                  <p className="text-xs text-[#6B7280] uppercase tracking-wide">Páginas visitadas</p>
                  <p className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{data.total_visits}</p>
                </div>
              </div>
            </div>

            <section>
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Páginas mais visitadas</h4>
              <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3F4F6] text-[#6B7280]">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Caminho</th>
                      <th className="px-3 py-2 text-right font-medium w-20">Visitas</th>
                      <th className="px-3 py-2 text-left font-medium">Última</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {data.top_pages.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-[#9CA3AF]">Sem visitas registadas.</td></tr>}
                    {data.top_pages.map((p, i) => (
                      <tr key={p.path + i} className="hover:bg-[#F9FAFB]">
                        <td className="px-3 py-2 text-[#111827] font-mono text-xs">{p.path}</td>
                        <td className="px-3 py-2 text-right font-semibold text-[#0F1829]">{p.visits}</td>
                        <td className="px-3 py-2 text-[#6B7280] text-xs">{fmtDate(p.last_visit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Últimas sessões de login</h4>
              <div className="border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB] max-h-64 overflow-auto">
                {data.login_sessions.length === 0 && <div className="px-3 py-6 text-center text-[#9CA3AF] text-sm">Sem logins registados.</div>}
                {data.login_sessions.map((s, i) => (
                  <div key={`${s.timestamp || 'ts'}-${s.ip || 'ip'}-${i}`} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-[#111827] font-medium whitespace-nowrap">
                      <Clock size={14} className="text-[#6B7280]" /> {fmtDate(s.timestamp)}
                    </div>
                    <div className="text-[#6B7280] truncate text-right flex-1">
                      <span className="font-mono">{s.ip || '?'}</span>
                      {s.user_agent && <span className="block text-[10px] text-[#9CA3AF] truncate">{s.user_agent}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
